import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Info, AlertTriangle } from 'lucide-react';
import type {
  ConditionSuggestion,
  ConditionSuggestionCategory,
  CreateSuggestionData,
  UpdateSuggestionData,
  ToolScope,
} from '../../../api/condition-suggestions.api';
import { ConditionSuggestionKind } from '../../../api/condition-suggestions.api';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Hard limit from the backend DTO (MaxLength 2000 characters). */
const MAX_CHARS = 2000;

/** Soft cap: words above this threshold trigger a non-blocking warning. */
const SOFT_CAP_WORDS = 500;

/** Human-readable labels for each tool scope. */
const TOOL_SCOPE_LABELS: Record<ToolScope, string> = {
  warm_transfer: 'Warm Transfer',
  outcall: 'Outcall',
  all: 'Alle Tools',
};

/**
 * Display labels for each suggestion kind shown as a read-only type badge.
 * Map to i18n-keys: condition_suggestions.modal.kind_pre_condition /
 * condition_suggestions.modal.kind_behavior — Agent 3 will wire these into
 * locale files once a translation layer is introduced.
 */
const KIND_BADGE_LABELS: Record<ConditionSuggestionKind, string> = {
  [ConditionSuggestionKind.PRE_CONDITION]: 'Pre-Condition',
  [ConditionSuggestionKind.BEHAVIOR_INSTRUCTION]: 'Behavior-Instruction',
};

/**
 * Colour classes per kind so admin can distinguish the two suggestion
 * libraries at a glance inside the modal badge.
 */
const KIND_BADGE_CLASSES: Record<ConditionSuggestionKind, string> = {
  [ConditionSuggestionKind.PRE_CONDITION]: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  [ConditionSuggestionKind.BEHAVIOR_INSTRUCTION]: 'bg-teal-100 text-teal-800 border-teal-200',
};

// ─── Component ────────────────────────────────────────────────────────────────

interface SuggestionFormModalProps {
  /** Open state of the dialog. */
  open: boolean;
  /** Called when the dialog requests to close (ESC, backdrop click, cancel). */
  onClose: () => void;
  /**
   * Suggestion to edit. When `null` the modal operates in "create" mode.
   * When set, all form fields are pre-filled from the suggestion.
   */
  editingSuggestion: ConditionSuggestion | null;
  /**
   * Directive type for this suggestion context.
   * In create mode this becomes the `kind` sent in the POST body and is
   * pre-populated from the active tab (PRE_CONDITION or BEHAVIOR_INSTRUCTION).
   * In edit mode this is derived from `editingSuggestion.kind` (read-only; pass
   * `editingSuggestion.kind` from the parent to ensure edit reflects truth).
   * Displayed as a read-only badge; kind cannot be changed via this modal.
   * i18n-key: condition_suggestions.modal.kind_label
   */
  kind: ConditionSuggestionKind;
  /** List of available categories for the Kategorie-Dropdown. */
  categories: ConditionSuggestionCategory[];
  /** Called after a successful save. Parent should refresh its list. */
  onSaved: () => void;
  /** Async create handler — resolves on success, throws on backend error. */
  onCreate: (dto: CreateSuggestionData) => Promise<ConditionSuggestion>;
  /** Async update handler — resolves on success, throws on backend error. */
  onUpdate: (id: string, dto: UpdateSuggestionData) => Promise<ConditionSuggestion>;
}

interface FormState {
  categoryId: string;
  text: string;
  toolScope: ToolScope;
  displayOrder: number;
  isActive: boolean;
}

interface FormErrors {
  categoryId?: string;
  text?: string;
  toolScope?: string;
  displayOrder?: string;
}

const EMPTY_FORM: FormState = {
  categoryId: '',
  text: '',
  toolScope: 'all',
  displayOrder: 0,
  isActive: true,
};

/** Count words in a string (splits on whitespace). */
function countWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

/**
 * Modal dialog for creating and editing condition suggestions.
 * Accepts `kind` as a required prop; the kind is displayed as a read-only
 * badge and included in every POST/PUT request body. Changing kind after
 * creation is intentionally not supported via this UI — admin must delete and
 * recreate in the target library.
 */
export function SuggestionFormModal({
  open,
  onClose,
  editingSuggestion,
  kind,
  categories,
  onSaved,
  onCreate,
  onUpdate,
}: SuggestionFormModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const wordCount = countWords(form.text);
  const charCount = form.text.length;
  const isOverSoftCap = wordCount > SOFT_CAP_WORDS;
  const isOverHardLimit = charCount > MAX_CHARS;

  // Sync form when switching between create / edit
  useEffect(() => {
    if (open) {
      if (editingSuggestion) {
        setForm({
          categoryId: editingSuggestion.categoryId,
          text: editingSuggestion.text,
          toolScope: editingSuggestion.toolScope,
          displayOrder: editingSuggestion.displayOrder,
          isActive: editingSuggestion.isActive,
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setErrors({});
      setServerError(null);
    }
  }, [open, editingSuggestion]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.categoryId) {
      next.categoryId = 'Bitte eine Kategorie auswählen.';
    }
    if (!form.text.trim()) {
      next.text = 'Vorschlagstext ist erforderlich.';
    } else if (form.text.length > MAX_CHARS) {
      next.text = `Text darf maximal ${MAX_CHARS} Zeichen lang sein.`;
    }
    if (!form.toolScope) {
      next.toolScope = 'Bitte einen Tool-Typ auswählen.';
    }
    if (form.displayOrder < 0 || !Number.isInteger(form.displayOrder)) {
      next.displayOrder = 'Reihenfolge muss eine positive ganze Zahl sein.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setServerError(null);

    try {
      if (editingSuggestion) {
        const dto: UpdateSuggestionData = {
          categoryId: form.categoryId,
          text: form.text,
          toolScope: form.toolScope,
          displayOrder: form.displayOrder,
          isActive: form.isActive,
          // kind is intentionally omitted — kind cannot be reclassified via the form.
        };
        await onUpdate(editingSuggestion.id, dto);
      } else {
        const dto: CreateSuggestionData = {
          categoryId: form.categoryId,
          text: form.text,
          toolScope: form.toolScope,
          displayOrder: form.displayOrder,
          isActive: form.isActive,
          // kind comes from the active tab — set at modal open time via the `kind` prop.
          kind,
        };
        await onCreate(dto);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const apiError = err as { message?: string | string[]; statusCode?: number };
      if (Array.isArray(apiError.message)) {
        setServerError(apiError.message.join(' '));
      } else {
        setServerError(apiError.message ?? 'Fehler beim Speichern.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isEditing = editingSuggestion !== null;
  const isPrecondition = kind === ConditionSuggestionKind.PRE_CONDITION;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="suggestion-modal-desc"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Vorschlag bearbeiten' : 'Neuer Vorschlag'}
          </DialogTitle>
          <DialogDescription id="suggestion-modal-desc">
            {isEditing
              ? 'Bearbeiten Sie die Felder des Bedingungs-Vorschlags.'
              : 'Erstellen Sie einen neuen Bedingungs-Vorschlag für die Plattform-Bibliothek.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Read-only type badge — i18n-key: condition_suggestions.modal.kind_label.
              Shows which suggestion library this entry belongs to. Kind cannot be
              changed after creation; admin must delete and recreate to reclassify. */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Typ:</span>
            <Badge
              variant="outline"
              className={`text-xs font-semibold px-2.5 py-0.5 ${KIND_BADGE_CLASSES[kind]}`}
              aria-label={`Vorschlagstyp: ${KIND_BADGE_LABELS[kind]}`}
            >
              {KIND_BADGE_LABELS[kind]}
            </Badge>
            {isEditing && (
              <span className="text-xs text-gray-400">(nicht änderbar)</span>
            )}
          </div>

          {/* Convention hint — kind-aware to guide the admin per directive type */}
          <div
            className="flex gap-3 rounded-md bg-blue-50 border border-blue-200 px-4 py-3"
            role="note"
            aria-label="Formulierungs-Konvention"
          >
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 space-y-1">
              <p className="font-medium">Formulierungs-Konvention</p>
              {isPrecondition ? (
                <>
                  <p>
                    <span className="text-green-700 font-medium">Richtig:</span> Als Voraussetzung
                    formulieren — z.B. &quot;Anrufer hat zugestimmt&quot; oder &quot;Auftrag wurde
                    bestätigt&quot;.
                  </p>
                  <p>
                    <span className="text-red-600 font-medium">Falsch:</span> Nicht als Verbot
                    formulieren — z.B. &quot;Kein Transfer ohne Zustimmung&quot;.
                  </p>
                  <p className="text-blue-700 text-xs mt-1">
                    True = Tool darf laufen. Der Vorschlagstext wird direkt in die Pre-Condition
                    des Tools geschrieben.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <span className="text-green-700 font-medium">Richtig:</span> Als
                    Verhaltensregel formulieren — z.B. &quot;Bestätige den Namen des Empfängers
                    vor dem Transfer&quot;.
                  </p>
                  <p>
                    <span className="text-red-600 font-medium">Falsch:</span> Nicht als
                    Gate-Bedingung formulieren — Behavior-Instructions sind keine Sperr-Regeln.
                  </p>
                  <p className="text-blue-700 text-xs mt-1">
                    Behavior-Instructions steuern das Verhalten während der Tool-Ausführung.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Suggestion text with live counter */}
          <div className="space-y-1">
            <Label htmlFor="sug-text">
              Vorschlagstext <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="sug-text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder={
                isPrecondition
                  ? 'z.B. "Der Anrufer hat einer Weitervermittlung ausdrücklich zugestimmt."'
                  : 'z.B. "Bestätige den vollständigen Namen des Transfer-Empfängers vor der Übergabe."'
              }
              rows={5}
              aria-invalid={!!errors.text}
              aria-describedby="sug-text-meta"
            />
            <div id="sug-text-meta" className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className={charCount > MAX_CHARS ? 'text-red-600 font-medium' : 'text-gray-500'}>
                  {charCount}/{MAX_CHARS} Zeichen
                </span>
                <span className="text-gray-400">·</span>
                <span className={isOverSoftCap ? 'text-amber-600 font-medium' : 'text-gray-500'}>
                  {wordCount} Wörter
                </span>
              </div>
              {isOverSoftCap && !isOverHardLimit && (
                <span className="flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Empfohlen: max. {SOFT_CAP_WORDS} Wörter
                </span>
              )}
            </div>
            {errors.text && (
              <p className="text-xs text-red-600" role="alert">{errors.text}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Tool scope */}
            <div className="space-y-1">
              <Label htmlFor="sug-tool-scope">
                Tool-Typ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.toolScope}
                onValueChange={(v) => setForm({ ...form, toolScope: v as ToolScope })}
              >
                <SelectTrigger id="sug-tool-scope" aria-invalid={!!errors.toolScope}>
                  <SelectValue placeholder="Tool-Typ auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(TOOL_SCOPE_LABELS) as [ToolScope, string][]).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.toolScope && (
                <p className="text-xs text-red-600" role="alert">{errors.toolScope}</p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-1">
              <Label htmlFor="sug-category">
                Kategorie <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.categoryId}
                onValueChange={(v) => setForm({ ...form, categoryId: v })}
                disabled={categories.length === 0}
              >
                <SelectTrigger id="sug-category" aria-invalid={!!errors.categoryId}>
                  <SelectValue placeholder="Kategorie auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.categoryId && (
                <p className="text-xs text-red-600" role="alert">{errors.categoryId}</p>
              )}
              {categories.length === 0 && (
                <p className="text-xs text-amber-600">
                  Bitte zuerst eine Kategorie anlegen.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            {/* Display order */}
            <div className="space-y-1">
              <Label htmlFor="sug-order">Anzeigereihenfolge</Label>
              <Input
                id="sug-order"
                type="number"
                min={0}
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value, 10) || 0 })}
                aria-invalid={!!errors.displayOrder}
              />
              <p className="text-xs text-gray-500">Niedrigere Werte zuerst.</p>
              {errors.displayOrder && (
                <p className="text-xs text-red-600" role="alert">{errors.displayOrder}</p>
              )}
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-3 pb-1">
              <Switch
                id="sug-active"
                checked={form.isActive}
                onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
              />
              <Label htmlFor="sug-active" className="cursor-pointer">
                Vorschlag aktiv
              </Label>
            </div>
          </div>

          {/* Server error */}
          {serverError && (
            <div
              className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {serverError}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isOverHardLimit}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : isEditing ? (
              'Speichern'
            ) : (
              'Erstellen'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
